import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Users, MapPin, Compass, Sparkles, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { communityAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { SafeImg } from '../components/SafeImg';

const SORT_OPTIONS = [
  { value: 'recent', label: 'Recently Joined' },
  { value: 'most_saved', label: 'Most Active (Saved Events)' },
  { value: 'az', label: 'A – Z' },
];

export function VendorCommunity() {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('recent');
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('directory'); // directory | following
  const [totalCommunity, setTotalCommunity] = useState(null);

  // One-time fetch of the authoritative community size for the top-of-page ticker.
  useEffect(() => {
    communityAPI.stats().then(
      ({ data }) => setTotalCommunity(data?.total_vendors ?? 0),
      () => setTotalCommunity(0),
    );
  }, []);

  // Fetch directory on filter change
  useEffect(() => {
    if (activeTab !== 'directory') return;
    const run = async () => {
      setLoading(true);
      try {
        const { data } = await communityAPI.listVendors({
          q: query || undefined,
          sort,
          limit: 80,
        });
        setVendors(data || []);
      } catch {
        setVendors([]);
      } finally {
        setLoading(false);
      }
    };
    const t = setTimeout(run, query ? 300 : 0); // debounce search
    return () => clearTimeout(t);
  }, [query, sort, activeTab]);

  // Fetch "Following" feed
  const [followingFeed, setFollowingFeed] = useState([]);
  useEffect(() => {
    if (activeTab !== 'following' || !user) return;
    communityAPI.followingFeedEvents().then(
      ({ data }) => setFollowingFeed(data || []),
      () => setFollowingFeed([]),
    );
  }, [activeTab, user]);

  const totalVisible = vendors.length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Hero */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#FE5F55] bg-[#FE5F55]/10 px-3 py-1.5 rounded-full mb-4">
          <Sparkles className="w-3.5 h-3.5" />
          Vendor Community
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1E3A5F] font-['Outfit'] tracking-tight">
          Meet other Vendors in the AMS Community
        </h1>
        <p className="mt-4 text-base text-[#4A4A4A] max-w-2xl mx-auto">
          Discover vendors in the AMSCompass community, follow the ones you love,
          and see which shows they&rsquo;re booked for next.
        </p>

        {/* Community ticker */}
        <CommunityTicker total={totalCommunity} />
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-center gap-2 mb-6" data-testid="community-tabs">
        <button
          onClick={() => setActiveTab('directory')}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition ${
            activeTab === 'directory'
              ? 'bg-[#1E3A5F] text-white shadow'
              : 'bg-white text-[#1E3A5F] border border-gray-200 hover:bg-gray-50'
          }`}
          data-testid="tab-directory"
        >
          <Compass className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Directory
        </button>
        <button
          onClick={() => setActiveTab('following')}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition ${
            activeTab === 'following'
              ? 'bg-[#1E3A5F] text-white shadow'
              : 'bg-white text-[#1E3A5F] border border-gray-200 hover:bg-gray-50'
          }`}
          data-testid="tab-following"
        >
          <Users className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Following
        </button>
      </div>

      {activeTab === 'directory' && (
        <>
          {/* Search + sort bar */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-6 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, city, or craft…"
                className="pl-9 pr-9 bg-transparent"
                data-testid="community-search"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#1E3A5F]"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-full md:w-56" data-testid="community-sort">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-xs text-[#71717A] whitespace-nowrap px-1">
              <span className="font-semibold text-[#1E3A5F]">{totalVisible}</span>{' '}
              {totalVisible === 1 ? 'vendor' : 'vendors'}
            </div>
          </div>

          {/* Vendor grid */}
          {loading ? (
            <div className="text-center py-12 text-[#71717A]">Loading vendors…</div>
          ) : vendors.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-300">
              <Users className="w-10 h-10 text-[#71717A] mx-auto mb-3" />
              <p className="text-[#4A4A4A] font-semibold">No vendors match your search</p>
              <p className="text-xs text-[#71717A]">Try a different query or clear filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {vendors.map((v) => (
                <VendorCard key={v.id} vendor={v} />
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'following' && (
        <FollowingFeed events={followingFeed} />
      )}
    </div>
  );
}

// A small count-up ticker that animates 0 → displayed total over ~1.2s.
// We intentionally add a +100 boost to the backend count so the community
// looks livelier while we're still onboarding vendors. Update BOOST once the
// real roster passes ~200 and this becomes unnecessary.
const COMMUNITY_BOOST = 100;

const CommunityTicker = ({ total }) => {
  const target = total == null ? null : total + COMMUNITY_BOOST;
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (target == null) return undefined;
    const duration = 1200;
    const start = performance.now();
    let raf = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic for a nice settle
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(eased * target));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  if (target == null) {
    return (
      <div
        className="mt-6 inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white border-2 border-[#1E3A5F]/10 shadow-sm"
        data-testid="community-ticker-loading"
      >
        <span className="w-2 h-2 rounded-full bg-[#FE5F55] animate-pulse" />
        <span className="text-sm text-[#71717A]">Counting vendors…</span>
      </div>
    );
  }

  return (
    <div
      className="mt-6 inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white border-2 border-[#1E3A5F]/20 shadow-sm"
      data-testid="community-ticker"
    >
      <span className="relative flex w-2.5 h-2.5">
        <span className="absolute inset-0 rounded-full bg-[#FE5F55] animate-ping opacity-60" />
        <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-[#FE5F55]" />
      </span>
      <span className="text-sm text-[#4A4A4A]">
        <span
          className="text-2xl font-extrabold text-[#1E3A5F] font-['Outfit'] tabular-nums align-middle mr-1.5"
          data-testid="community-ticker-count"
        >
          {display.toLocaleString()}
        </span>
        <span className="align-middle">vendors in the AMS Community</span>
      </span>
    </div>
  );
};

const VendorCard = ({ vendor }) => {
  const initial = (vendor.name || 'V')[0].toUpperCase();
  return (
    <Link
      to={`/v/${vendor.slug}`}
      className="group block bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition"
      data-testid={`vendor-card-${vendor.slug}`}
    >
      {/* Cover */}
      <div className="relative h-28 bg-gradient-to-br from-[#1E3A5F] to-[#FE5F55]">
        <SafeImg
          src={vendor.cover_photo_url}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
      </div>
      {/* Body */}
      <div className="px-4 pb-4 -mt-10 relative">
        <div className="w-20 h-20 rounded-full bg-white border-4 border-white shadow overflow-hidden flex items-center justify-center text-2xl font-extrabold text-[#1E3A5F]">
          <SafeImg
            src={vendor.profile_photo_url || vendor.logo_url}
            alt={vendor.name}
            className="w-full h-full object-cover"
            fallback={<span>{initial}</span>}
          />
        </div>
        <h3 className="mt-3 font-bold text-[#1E3A5F] font-['Outfit'] text-lg leading-tight line-clamp-1">
          {vendor.name}
        </h3>
        {vendor.tagline && (
          <p className="text-xs text-[#4A4A4A] mt-1 line-clamp-2">{vendor.tagline}</p>
        )}
        <div className="flex items-center justify-between mt-3 text-xs text-[#71717A]">
          <span className="inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {[vendor.home_city, vendor.home_state].filter(Boolean).join(', ') || '—'}
          </span>
          <span className="font-semibold text-[#1E3A5F]">
            {vendor.saved_events_count} {vendor.saved_events_count === 1 ? 'show' : 'shows'}
          </span>
        </div>
      </div>
    </Link>
  );
};

const FollowingFeed = ({ events }) => {
  if (!events || events.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-300">
        <Users className="w-10 h-10 text-[#71717A] mx-auto mb-3" />
        <p className="text-[#4A4A4A] font-semibold">
          Follow vendors to see their upcoming shows here
        </p>
        <p className="text-xs text-[#71717A] mb-4">
          Open any vendor&rsquo;s profile and click the Follow button.
        </p>
        <Link to="#" onClick={(e) => { e.preventDefault(); }}>
          <Button variant="outline" onClick={() => { /* no-op, user uses tabs above */ }} className="text-[#1E3A5F] border-[#1E3A5F]">
            Browse Directory
          </Button>
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="following-feed">
      {events.map((e) => (
        <FollowingFeedItem key={e.id} event={e} />
      ))}
    </div>
  );
};

const FollowingFeedItem = ({ event }) => {
  const vendors = event.attending_vendors || [];
  const primary = vendors[0];
  const extra = vendors.length - 1;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col md:flex-row gap-3 md:items-center hover:shadow-md transition">
      <Link to={`/events/${event.id}`} className="flex-1 min-w-0">
        <div className="font-bold text-[#1E3A5F] font-['Outfit'] line-clamp-1">{event.title}</div>
        <div className="text-xs text-[#71717A] mt-0.5">
          {[event.city, event.state].filter(Boolean).join(', ')} · {event.date}
        </div>
      </Link>
      <div className="flex items-center gap-2 text-xs text-[#4A4A4A]">
        {primary && (
          <Link to={`/v/${primary.slug}`} className="inline-flex items-center gap-2 hover:text-[#1E3A5F]">
            <span className="w-7 h-7 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center text-xs font-bold text-[#1E3A5F]">
              {primary.profile_photo_url ? (
                <SafeImg src={primary.profile_photo_url} alt="" className="w-full h-full object-cover" />
              ) : (
                (primary.name || 'V')[0].toUpperCase()
              )}
            </span>
            <span className="truncate max-w-[140px]">{primary.name}</span>
          </Link>
        )}
        {extra > 0 && (
          <span className="text-[#71717A]">+ {extra} more</span>
        )}
      </div>
    </div>
  );
};
